import { useEffect, useState } from "preact/hooks";
import { api } from "../api";
import type { Correlation, RoutineToday, StreakInfo } from "@hh/shared";

const fmtSleep = (m: number | null) => (m == null ? "—" : `${Math.floor(m / 60)}h ${m % 60}m`);

interface ChainDef { key: "walk_done" | "breakfast_done" | "shower_done"; emoji: string; label: string }
const CHAIN: ChainDef[] = [
  { key: "walk_done", emoji: "🐕", label: "Morning walk" },
  { key: "breakfast_done", emoji: "🍳", label: "Breakfast" },
  { key: "shower_done", emoji: "🚿", label: "Showered & ready" },
];

export function Routine({ reloadKey, onChanged }: { reloadKey: number; onChanged: () => void }) {
  const [r, setR] = useState<RoutineToday | null>(null);
  const [streak, setStreak] = useState<StreakInfo | null>(null);
  const [patterns, setPatterns] = useState<Correlation[]>([]);

  function load() {
    api.routineToday().then(setR).catch(() => {});
    api.streak().then(setStreak).catch(() => {});
    api.correlations().then((c) => setPatterns(c.correlations)).catch(() => {});
  }
  useEffect(load, [reloadKey]);

  async function toggle(key: ChainDef["key"]) {
    if (!r) return;
    const next = !r[key];
    setR({ ...r, [key]: next });
    await api.routineUpdate({ [key]: next });
    onChanged();
    api.streak().then(setStreak).catch(() => {});
  }

  async function markWoke() {
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    await api.routineUpdate({ woke_time: hhmm });
    load(); onChanged();
  }
  async function markLightsOut() {
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    await api.routineUpdate({ lights_out: hhmm });
    load();
  }

  return (
    <div class="app-shell fade-in">
      <div class="screen-head"><div><div class="eyebrow">Early riser</div><h1>Routine</h1></div></div>

      {/* Identity streak */}
      {streak && (
        <div class="card">
          <div class="streak-hero">
            <div class="flame">{streak.current_streak > 0 ? "🔥" : "🌱"}</div>
            <div class="n">{streak.current_streak}</div>
            <div class="cap">day streak · showed up{streak.longest_streak > streak.current_streak ? ` · best ${streak.longest_streak}` : ""}</div>
          </div>
          <div class="identity">
            {streak.upBeforeTargetLast7 >= 1
              ? `${streak.upBeforeTargetLast7} of the last 7 days up before ${streak.wakeTarget} — you're becoming this guy.`
              : `Target wake is ${streak.wakeTarget}. Win one morning and the chain starts.`}
          </div>
        </div>
      )}

      {/* Morning chain */}
      {r && (
        <div class="card">
          <h2>Morning chain</h2>
          <div class="chain-item">
            <button class={`chain-check ${r.woke_time ? "on" : ""}`} onClick={markWoke}>✓</button>
            <span class="emoji">⏰</span>
            <span class="lbl">Woke up{r.woke_time ? ` · ${r.woke_time}` : ""}</span>
            <span class="muted">target {r.wakeTarget}</span>
          </div>
          {CHAIN.map((c) => (
            <div class={`chain-item ${r[c.key] ? "done" : ""}`} key={c.key}>
              <button class={`chain-check ${r[c.key] ? "on" : ""}`} onClick={() => toggle(c.key)}>✓</button>
              <span class="emoji">{c.emoji}</span>
              <span class="lbl">{c.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Last night (watch) + wind-down */}
      {r && (
        <div class="card">
          <h2>Sleep & wind-down</h2>
          <div class="stat-grid">
            <div class="stat"><div class="v">{fmtSleep(r.sleepMinutes)}</div><div class="l">last night</div></div>
            <div class="stat"><div class="v">{r.healthWake ?? r.woke_time ?? "—"}</div><div class="l">wake</div></div>
          </div>
          <p class="muted" style={{ marginTop: 12 }}>
            Mornings are won the night before. Aiming for {r.wakeTarget} — screens down ~90 min prior, watch on the charger to start winding down.
          </p>
          <button class="btn secondary" style={{ marginTop: 10 }} onClick={markLightsOut}>
            {r.lights_out ? `Lights out logged · ${r.lights_out}` : "Log lights out now"}
          </button>
        </div>
      )}

      {/* Patterns (correlation engine) */}
      <div class="card">
        <h2>Patterns</h2>
        {patterns.length === 0 ? (
          <p class="muted">Your patterns will surface here as you log.</p>
        ) : (
          patterns.map((p) => (
            <div class={`patt ${p.enoughData ? "" : "low"}`} key={p.kind}>
              <div class="h">{p.headline}</div>
              <div class="d">{p.detail}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
